import PersonAddAlt1Rounded from '@mui/icons-material/PersonAddAlt1Rounded';
import PlaylistAddRounded from '@mui/icons-material/PlaylistAddRounded';
import {
  Alert,
  Box,
  Button,
  Card,
  Divider,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useReducer, useRef, useState, type ReactNode } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { ApiError } from '../../../api/client';
import { useAuth } from '../../../auth/AuthProvider';
import { ConfirmDialog } from '../../../components/ConfirmDialog';
import { Money } from '../../../components/Money';
import { ContactPicker, type Contact } from '../../../components/pickers';
import { useShop } from '../../../shop/ShopProvider';
import {
  productQuery,
  shopToday,
  usePricing,
  useSellableLocations,
  useTaxRates,
  type VariantSummary,
} from '../lib/api';
import {
  cartItemFromVariant,
  cartNeedsDiscount,
  cartProblems,
  cartReducer,
  cartTotals,
  emptyCart,
  type CartState,
} from '../lib/cart';
import { dec } from '../lib/decimal';
import {
  documentHeaderSchema,
  headerField,
  type DocumentHeader,
  type EditorKind,
} from '../lib/editorSchema';
import type { EditorValues } from '../lib/requests';
import { SumRow } from '../counter/bits';
import { ProductSearch, type ProductSearchHandle } from '../counter/ProductSearch';
import { CustomerAccountCard, useCreditCheck, useCustomerAccount } from './CustomerAccount';
import { DiscountInput } from './DiscountInput';
import { LineGrid } from './LineGrid';
import { QuickCustomerDialog } from '../../../components/QuickCustomerDialog';

export type { EditorValues };

export interface EditorAction {
  label: string;
  /** Primary (contained) or secondary. */
  primary?: boolean;
  icon?: ReactNode;
  run: (values: EditorValues) => Promise<unknown>;
  /** Ask first (posting): title and the financial effect. */
  confirm?: (values: EditorValues, total: string) => { title: string; body: ReactNode };
}

/**
 * One-screen editor for a sales invoice or order: customer, dates, item lines with inline unit, price,
 * discount and tax, a document discount and live totals that match what the server will post.
 */
export function DocumentEditor({
  kind,
  initialHeader,
  initialCart = emptyCart,
  actions,
  draftKey,
}: {
  kind: EditorKind;
  initialHeader?: Partial<DocumentHeader>;
  initialCart?: CartState;
  actions: EditorAction[];
  /** localStorage key to keep unsaved lines (new documents only). */
  draftKey?: string;
}) {
  const { can } = useAuth();
  const { format } = useShop();
  const qc = useQueryClient();
  const pricing = usePricing();
  const taxRates = useTaxRates();
  const locations = useSellableLocations();
  const search = useRef<ProductSearchHandle>(null);
  const [newCustomer, setNewCustomer] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [apiErrors, setApiErrors] = useState<string[]>([]);
  const [submitted, setSubmitted] = useState(false);
  const [pending, setPending] = useState<{ action: EditorAction; values: EditorValues } | null>(
    null,
  );

  const [cart, dispatch] = useReducer(cartReducer, initialCart, (init) => {
    if (!draftKey) return init;
    try {
      const raw = localStorage.getItem(draftKey);
      if (raw) {
        const saved = JSON.parse(raw) as CartState;
        if (Array.isArray(saved.lines)) return { ...saved, revision: 0 };
      }
    } catch {
      /* ignore */
    }
    return init;
  });

  useEffect(() => {
    if (!draftKey) return;
    try {
      if (cart.lines.length) localStorage.setItem(draftKey, JSON.stringify(cart));
      else localStorage.removeItem(draftKey);
    } catch {
      /* storage unavailable */
    }
  }, [cart, draftKey]);

  const form = useForm<DocumentHeader>({
    resolver: zodResolver(documentHeaderSchema),
    mode: 'onBlur',
    defaultValues: {
      contact: null,
      docDate: shopToday(format.timeZone),
      later: '',
      locationId: '',
      reference: '',
      notes: '',
      ...initialHeader,
    },
  });
  const contact = useWatch({ control: form.control, name: 'contact' }) as Contact | null;
  const { errors } = form.formState;

  const totals = useMemo(() => cartTotals(cart, pricing), [cart, pricing]);
  const problems = cartProblems(cart, totals);
  // Posting an invoice puts its total on the customer's account (the server checks the credit limit).
  const named = contact && !contact.isWalkIn ? contact : null;
  const account = useCustomerAccount(named?.id, named);
  const credit = useCreditCheck(account, totals.total);
  const creditBlocked = kind === 'invoice' && credit.blocked;
  const discountBlocked = !can('sales.discount') && cartNeedsDiscount(cart);
  const lineIssues = [
    ...(cart.lines.length === 0 && submitted ? ['Add at least one item.'] : []),
    ...(discountBlocked
      ? ['A price below the list price or a discount needs the “Give discounts” permission.']
      : []),
    ...problems,
  ];

  const addVariant = async (v: VariantSummary, qty: string) => {
    const product = await qc.query(productQuery(v.productId)).catch(() => null);
    dispatch({ type: 'add', item: cartItemFromVariant(v, product), qty });
  };

  const execute = async (action: EditorAction, values: EditorValues) => {
    setBusy(action.label);
    setApiErrors([]);
    try {
      await action.run(values);
      if (draftKey)
        try {
          localStorage.removeItem(draftKey);
        } catch {
          /* ignore */
        }
    } catch (e) {
      if (e instanceof ApiError) {
        const rest: string[] = [];
        for (const d of e.details) {
          const field = headerField(d.path);
          if (field) form.setError(field, { message: d.message });
          else rest.push(`${d.path}: ${d.message}`);
        }
        setApiErrors([e.message, ...rest]);
      } else setApiErrors(['Something went wrong. Please try again.']);
    } finally {
      setBusy(null);
    }
  };

  const run = (action: EditorAction) =>
    form.handleSubmit(
      async (header) => {
        setSubmitted(true);
        if (cart.lines.length === 0 || problems.length || discountBlocked || creditBlocked) return;
        const values = { header: header as EditorValues['header'], cart };
        if (action.confirm) setPending({ action, values });
        else await execute(action, values);
      },
      () => setSubmitted(true),
    );

  // ⌘S / Ctrl+S runs the primary action; F2 jumps to the item search.
  const primary = actions.find((a) => a.primary) ?? actions[0];
  const runRef = useRef(run);
  useEffect(() => {
    runRef.current = run;
  });
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's' && primary) {
        e.preventDefault();
        void runRef.current(primary)();
      } else if (e.key === 'F2') {
        e.preventDefault();
        search.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [primary]);

  const terms = contact?.paymentTermsDays;
  const showLocation = kind === 'order' && (locations.data?.length ?? 0) > 1;

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: 'minmax(0,1fr)', lg: 'minmax(0,1fr) 360px' },
        gap: 3,
        alignItems: 'start',
      }}
    >
      <Stack spacing={3} sx={{ minWidth: 0 }}>
        <Card sx={{ p: 2.5 }}>
          <Typography variant="h5" sx={{ mb: 2 }}>
            Customer & dates
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'minmax(0,1fr)', sm: '1fr 1fr', md: '2fr 1fr 1fr' },
              gap: 2,
            }}
          >
            <Stack
              direction="row"
              spacing={1}
              sx={{ alignItems: 'flex-start', gridColumn: { sm: '1 / -1', md: 'auto' } }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Controller
                  control={form.control}
                  name="contact"
                  render={({ field }) => (
                    <ContactPicker
                      type="customer"
                      allowCreate
                      value={field.value as Contact | null}
                      onChange={(c) => field.onChange(c)}
                      error={!!errors.contact}
                      helperText={errors.contact?.message as string | undefined}
                    />
                  )}
                />
              </Box>
              {can('customers.create') && (
                <Tooltip title="New customer">
                  <IconButton
                    aria-label="New customer"
                    onClick={() => setNewCustomer(true)}
                    sx={{ mt: 0.5, border: 1, borderColor: 'divider', borderRadius: '12px' }}
                  >
                    <PersonAddAlt1Rounded />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
            <TextField
              type="date"
              label={kind === 'invoice' ? 'Invoice date' : 'Order date'}
              {...form.register('docDate')}
              error={!!errors.docDate}
              helperText={errors.docDate?.message}
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <TextField
              type="date"
              label={kind === 'invoice' ? 'Due date' : 'Expected delivery'}
              {...form.register('later')}
              error={!!errors.later}
              helperText={
                errors.later?.message ??
                (kind === 'invoice'
                  ? terms != null
                    ? `Empty = ${String(terms)}-day terms`
                    : 'Empty = customer’s terms'
                  : 'Optional')
              }
              slotProps={{ inputLabel: { shrink: true } }}
            />
            {showLocation && (
              <TextField
                select
                label="Deliver from"
                {...form.register('locationId')}
                defaultValue={form.getValues('locationId')}
              >
                <MenuItem value="">Default location</MenuItem>
                {locations.data?.map((l) => (
                  <MenuItem key={l.id} value={l.id}>
                    {l.name}
                  </MenuItem>
                ))}
              </TextField>
            )}
            <TextField
              label="Reference"
              placeholder="Customer PO, phone order…"
              {...form.register('reference')}
              error={!!errors.reference}
              helperText={errors.reference?.message}
            />
          </Box>
        </Card>

        <Card sx={{ overflow: 'visible' }}>
          <Box sx={{ p: 2.5, pb: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Stack
              direction="row"
              sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}
            >
              <Typography variant="h5">Items</Typography>
              <Typography variant="caption" color="text.secondary">
                {cart.lines.length} {cart.lines.length === 1 ? 'line' : 'lines'}
              </Typography>
            </Stack>
            <ProductSearch
              ref={search}
              onPick={(v, q) => void addVariant(v, q)}
              onNotFound={() => undefined}
            />
          </Box>
          {cart.lines.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6, px: 3 }}>
              <PlaylistAddRounded sx={{ fontSize: 40, color: 'text.disabled' }} />
              <Typography sx={{ mt: 1, fontWeight: 600 }}>No items yet</Typography>
              <Typography variant="body2" color="text.secondary">
                Search above by name, SKU or barcode to add the first line.
              </Typography>
            </Box>
          ) : (
            <LineGrid
              lines={cart.lines}
              totals={totals.lines}
              taxRates={taxRates.data ?? []}
              taxEnabled={pricing.taxEnabled}
              dispatch={dispatch}
            />
          )}
          <Box sx={{ p: 2.5 }}>
            <TextField
              label="Notes"
              placeholder="Printed on the document"
              fullWidth
              multiline
              minRows={2}
              {...form.register('notes')}
              error={!!errors.notes}
              helperText={errors.notes?.message}
            />
          </Box>
        </Card>
      </Stack>

      <Card sx={{ position: { lg: 'sticky' }, top: { lg: 88 }, p: 2.5 }}>
        <Typography variant="h5" sx={{ mb: 1.5 }}>
          Summary
        </Typography>
        <SumRow label="Subtotal" value={<Money value={totals.subtotal} />} muted />
        <Box sx={{ py: 1 }}>
          <DiscountInput
            size="small"
            label="Discount on document"
            value={cart.discount}
            disabled={!can('sales.discount') || cart.lines.length === 0}
            onChange={(discount) => dispatch({ type: 'discount', discount })}
          />
        </Box>
        {dec(totals.discountTotal) > 0n && (
          <SumRow
            label="Discounts"
            value={
              <Box component="span" sx={{ color: 'success.main' }}>
                −<Money value={totals.discountTotal} />
              </Box>
            }
            muted
          />
        )}
        {pricing.taxEnabled && (
          <SumRow
            label={pricing.pricesIncludeTax ? 'Tax (included)' : 'Tax'}
            value={<Money value={totals.taxTotal} />}
            muted
          />
        )}
        <Divider sx={{ my: 1.5 }} />
        <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
          <Typography variant="h5">Total</Typography>
          <Typography variant="h3" className="tabular" data-testid="editor-total">
            <Money value={totals.total} />
          </Typography>
        </Stack>

        {named && credit.check && (
          <Box sx={{ mt: 2 }}>
            <CustomerAccountCard
              name={named.name}
              check={credit.check}
              adding={totals.total}
              blocked={creditBlocked}
              dense
            />
          </Box>
        )}

        {lineIssues.length > 0 && (cart.lines.length > 0 || submitted) && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {lineIssues[0]}
          </Alert>
        )}
        {apiErrors.length > 0 && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {apiErrors.map((m) => (
              <div key={m}>{m}</div>
            ))}
          </Alert>
        )}

        <Stack spacing={1.25} sx={{ mt: 2.5 }}>
          {[...actions]
            .sort((a, b) => Number(!!b.primary) - Number(!!a.primary))
            .map((a) => (
              <Button
                key={a.label}
                fullWidth
                size="large"
                variant={a.primary ? 'contained' : 'outlined'}
                startIcon={a.icon}
                loading={busy === a.label}
                disabled={(!!busy && busy !== a.label) || creditBlocked}
                onClick={() => void run(a)()}
                sx={{ minHeight: 48 }}
              >
                {a.label}
              </Button>
            ))}
        </Stack>
      </Card>

      {pending?.action.confirm && (
        <ConfirmDialog
          open
          {...pending.action.confirm(pending.values, totals.total)}
          confirmLabel={pending.action.label}
          onClose={() => setPending(null)}
          onConfirm={() => execute(pending.action, pending.values)}
        />
      )}

      <QuickCustomerDialog
        open={newCustomer}
        onClose={() => setNewCustomer(false)}
        onCreated={(c) => {
          form.setValue('contact', c, { shouldValidate: true });
          setNewCustomer(false);
        }}
      />
    </Box>
  );
}
